import { createStore, createEffect } from "effector"

import ApiService from "services/ApiService"
import { ApiLinkEntity, ApiLinkRel, ApiServiceName, UserPermission } from "config/constants"
import { IApiLink } from "@/types"
import useCurrentUser from "hooks/useCurrentUser"
import { useCallback } from "react"
import { Role } from "models/UserRoles"

interface IPermissionKey {
    entity: ApiLinkEntity
    rel: ApiLinkRel
}

interface IPermissionKeyWithService extends IPermissionKey {
    service: ApiServiceName
}

type PermissionsStore = Map<string, boolean>

// Stores
const $permissions = createStore<Map<string, boolean>>(new Map())

// Effects
const initGlobalPermissions = createEffect(async (): Promise<PermissionsStore> => {
    const globalPermissionKeys: IPermissionKeyWithService[] = [
        {
            service: ApiServiceName.article,
            entity: ApiLinkEntity.productHierarchy,
            rel: ApiLinkRel.new,
        },
        {
            service: ApiServiceName.asset,
            entity: ApiLinkEntity.assetProduct,
            rel: ApiLinkRel.all,
        },
    ]

    const checks: (Promise<void>)[] = []
    const result: PermissionsStore = new Map()

    for (let key of globalPermissionKeys) {
        checks.push((async () => {
            const { link } = await ApiService.findLink({
                serviceName: key.service,
                followLink: {
                    entity: key.entity,
                    rel: key.rel,
                }
            })
            result.set(flattenPermissionWithServiceKey(key), !!link)
        })())
    }

    await Promise.all(checks)

    return result
})

const checkPermission = createEffect(async (key: IPermissionKeyWithService): Promise<{
    key: IPermissionKeyWithService,
    value: boolean
}> => {
    // check for existing permission that has already been checked before
    const permissions = $permissions.getState()
    const hasPermission = permissions.get(flattenPermissionKey(key))
    if (typeof hasPermission !== "undefined") {
        return {
            key,
            value: hasPermission,
        }
    }

    // check permission for the first time
    const link = await ApiService.findLink({
        serviceName: key.service,
        followLink: {
            entity: key.entity,
            rel: key.rel
        }
    })
    return {
        key,
        value: !!link,
    }
})

const checkPermissionByLinks = createEffect(async (key: IPermissionKey, links: IApiLink[]) => {
    return !!links
        .find(lnk => {
            return lnk.entity === key.entity
                && lnk.rel === key.rel
        })
})

// Listeners
$permissions.on(initGlobalPermissions.doneData, (_, payload) => payload)
$permissions.on(checkPermission.doneData, (state, payload) => {
    const permissions = new Map(state)
    permissions.set(flattenPermissionKey(payload.key), payload.value)
    return permissions
})


// Helpers
function flattenPermissionKey(key: IPermissionKey) {
    return `${key.entity}_${key.rel}`
}

function flattenPermissionWithServiceKey(key: IPermissionKeyWithService) {
    return `${key.service}_${key.entity}_${key.rel}`
}

// check global permission syncronously as they're fetched in the common store during app initialization
const checkGlobalPermission = (key: IPermissionKeyWithService): boolean => {
    const permissions = $permissions.getState()
    const hasPermission = permissions.get(flattenPermissionWithServiceKey(key))
    return hasPermission || false
}

// Exports
export default {
    $permissions,
    initGlobalPermissions,
    checkPermission,
    checkPermissionByLinks,
}

export function usePermissions() {
    const { user } = useCurrentUser()

    const hasPermission = useCallback((permission: UserPermission) => {
        return user?.hasPermission(permission) || false
    }, [user])

    const hasRole = useCallback((role: Role) => {
        return user?.hasRole(role) || false
    }, [user])

    return {
        checkGlobalPermission,
        checkPermission,
        checkPermissionByLinks,
        hasPermission,
        hasRole
    }
}
